// @flow
import React, { useState, useMemo, useCallback } from 'react';
import type { Car as CarType } from '../../../../types';
import { TYPE } from '../../../../constants/targetConstants';
import { degToRad } from '../../../../utils';
import { createGeometry } from '../../../../utils/targetUtils';
import Feature from '../../../ui/openlayers/Feature';
import { getStyle, useTargetStyle } from '../style';
import DraggableLabel from '../DraggableLabel';
import Trail from '../Trail';
import { targetStyles, draggableLabelStyles, labelConnectorStyles } from './style';


export type Props = $ReadOnly<{|
    icao: string,
    dataSource: 'auto' | 'system' | 'device',
    carBuffer?: $ReadOnlyArray<CarType>,
    heading?: number,
    isActive: boolean,
    showLabel: boolean,
    showTrails: boolean,
    showDisconnected?: boolean,
    debug?: boolean,
    dispatch?: any,

    selectTarget: (target: null | $ReadOnly<{id: string, type: typeof TYPE.car}>) => void
|}>;

function Car({ icao, dataSource, carBuffer, heading, isActive, showLabel, showTrails, showDisconnected, debug, selectTarget }: Props) {
    const [selected, setSelected] = useState(false);
    const car = carBuffer && carBuffer[0];
    const geometry = useMemo(() => car && createGeometry(car, degToRad(heading || 0)), [car, heading]);
    const label = car && car.TargetIdentification;
    const rotation = degToRad(heading || 0);
    const style = useTargetStyle(getStyle(targetStyles, selected, dataSource), { label, rotation });


    const properties = useMemo(() => ({
        type: 'target',
        targetType: TYPE.car,
        icao: icao,
        dataSource: dataSource,
        label: label
    }), [icao, dataSource, label]);



    const onSelect = useCallback(() => {
        setSelected(true);
        selectTarget({
            id: icao,
            type: TYPE.car
        });
    }, [selectTarget, icao]);
    const onDeselect = useCallback(() => {
        setSelected(false);
        selectTarget(null);
    }, [selectTarget]);

    if(!car) {
        if(!debug) {
            console.warn(`Entity data not found for car "${icao}". Data source: ${dataSource}`);
        }

        return null;
    }

    if(!isActive && !showDisconnected) {
        return null;
    }

    return (
        <React.Fragment>
            <Feature
                style={style}
                geometry={geometry}
                properties={properties}
                onSelect={onSelect}
                onDeselect={onDeselect}
            />

            {showLabel &&
                <DraggableLabel
                    targetType={TYPE.car}
                    target={car}
                    selected={selected}
                    dataSource={dataSource}
                    labelStyles={draggableLabelStyles}
                    connectorStyles={labelConnectorStyles}
                    onSelect={onSelect}
                    onDeselect={onDeselect}
                />
            }
            {showTrails && carBuffer && <Trail buffer={carBuffer} />}
        </React.Fragment>
    );
}

export default Car;